/**
 * ERROR BOUNDARY — Cattura errori di rendering React.
 *
 * Class component (obbligatorio anche in React 19 per error boundaries).
 * Applicato a due livelli:
 * 1. AppShell: wrappa <Outlet/> — errori nelle rotte non crashano sidebar/topbar
 * 2. AppRouter: wrappa componenti lazy — cattura chunk load failure
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import i18next from 'i18next';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Se true, mostra un fallback compatto (per sezioni interne) */
  compact?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      if (this.props.compact) {
        return (
          <div className="error-compact">
            <p className="mb-sm">{i18next.t('error.sectionError')}</p>
            <Button onClick={this.handleReset} variant="outlined" size="small">
              {i18next.t('error.retry')}
            </Button>
          </div>
        );
      }

      return (
        <div className="flex justify-center items-center h-full min-h-300">
          <div className="text-center p-2xl max-w-400">
            <div className="error-icon">!</div>
            <h2 className="mb-sm text-xl">{i18next.t('error.title')}</h2>
            <p className="text-muted mb-xl">
              {this.state.error?.message || i18next.t('error.defaultMessage')}
            </p>
            <div className="flex gap-sm justify-center">
              <Button onClick={() => window.location.reload()} variant="contained">
                {i18next.t('error.reloadPage')}
              </Button>
              <Button variant="outlined" component={Link} to="/tasks">
                {i18next.t('error.backHome')}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
